/**
 * User service aligned with old version API patterns
 */

import { CUSTOMER_API_BASE_URL } from '../config/env';


// Re-export types for backward compatibility
export type UserRole = 'admin' | 'creator' | 'viewer' | 'waitlisted';

export interface RegisterResponse {
  success: boolean;
  data: {
    success?: boolean;    // Sometimes there's nested success
    data?: {              // Sometimes there's nested data
      user: {
        userId: string;
        email: string;
        phoneNumber?: string;
        lastLoginAt: string;
      };
      role: string;
      needsProfile: boolean;
    };
    user?: {              // Sometimes data is at this level
      userId: string;
      email: string;
      phoneNumber?: string;
      lastLoginAt: string;
    };
    role?: string;        // Sometimes role is at this level
    needsProfile?: boolean; // Sometimes needsProfile is at this level
  };
  message?: string;
}

export interface ProfileData {
  userId: string;
  fullName: string;
  email: string;
  studioName: string;
  jobTitle?: string; // user's self-declared title (not account role)
  phoneNumber: string;
  productionType?: string;
  project?: string;
  softwareTools?: string[];
  exportFormat?: string;
}

/**
 * Register a new user after authentication
 * Cookie-based auth: token is sent automatically via HttpOnly cookie
 * @param email User's email address
 * @returns RegisterResponse with user data and role information
 */
export const registerUser = async (email: string): Promise<RegisterResponse> => {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/register`, {
    method: 'POST',
    credentials: 'include', // Include cookies in the request
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })  // Send only email like old version
  });

  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.error || 'Registration failed');
    } catch (e) {
      throw new Error(`Registration failed: ${errorText.substring(0, 100)}`);
    }
  }

  return response.json();
};

/**
 * Create or update a user profile
 * Cookie-based auth: token is sent automatically via HttpOnly cookie
 * @param profileData Profile data to save
 * @returns Profile data with isNewProfile flag
 */
export const saveUserProfile = async (
  profileData: ProfileData
): Promise<{ profile: ProfileData; isNewProfile: boolean }> => {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/profiles`, {
    method: 'POST',
    credentials: 'include', // Include cookies in the request
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(profileData)
  });


  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.error || 'Profile creation failed');
    } catch (e) {
      throw new Error(`Profile creation failed: ${errorText.substring(0, 100)}`);
    }
  }

  return response.json();
};

/**
 * Get the current user's profile
 * Cookie-based auth: token is sent automatically via HttpOnly cookie
 * @returns Profile data
 */
export const getUserProfile = async (): Promise<ProfileData> => {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/profiles`, {
    method: 'GET',
    credentials: 'include', // Include cookies in the request
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Profile not found');
    }
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.error || 'Failed to get profile');
    } catch (e) {
      throw new Error(`Failed to get profile: ${errorText.substring(0, 100)}`);
    }
  }

  return response.json();
};

/**
 * Update an existing user profile
 * Cookie-based auth: token is sent automatically via HttpOnly cookie
 * @param profileData Partial profile data to update
 * @returns Updated profile data
 */
export const updateUserProfile = async (
  profileData: Partial<Omit<ProfileData, 'userId'>>
): Promise<ProfileData> => {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/profiles`, {
    method: 'PATCH',
    credentials: 'include', // Include cookies in the request
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(profileData)
  });

  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.error || 'Profile update failed');
    } catch (e) {
      throw new Error(`Profile update failed: ${errorText.substring(0, 100)}`);
    }
  }

  return response.json();
};

/**
 * Check if the current user has a complete profile
 * Cookie-based auth: token is sent automatically via HttpOnly cookie
 * @returns True if profile exists, false otherwise
 */
export const hasUserProfile = async (): Promise<boolean> => {
  try {
    await getUserProfile();
    return true;
  } catch (error) {
    if (error instanceof Error && error.message === 'Profile not found') {
      return false;
    }
    throw error;
  }
};

/**
 * Check if user has signed up (has userId in whitelist)
 * Cookie-based auth: This is a public endpoint, no auth required
 * @param email The email to check
 * @returns true if user has completed signup (has account), false if only in waitlist or not found
 */


/**
 * Check user signup status in whitelist
 * @param email The email to check
 * @returns Object with detailed status information
 */
export const checkWhitelistSignedUp = async (email: string): Promise<{
  hasSignedUp: boolean;
  inWhitelist: boolean;
  status: 'signed_up' | 'waitlisted' | 'not_found';
}> => {
  try {
    const response = await fetch(
      `${CUSTOMER_API_BASE_URL}/api/whitelist/signed-up/${encodeURIComponent(email)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      console.error('[checkWhitelistSignedUp] API error:', response.status);
      return {
        hasSignedUp: false,
        inWhitelist: false,
        status: 'not_found'
      };
    }

    const data = await response.json();
    return {
      hasSignedUp: data?.hasSignedUp || false,
      inWhitelist: data?.inWhitelist || false,
      status: data?.status || 'not_found'
    };
  } catch (error) {
    console.error('[checkWhitelistSignedUp] Error:', error);
    return {
      hasSignedUp: false,
      inWhitelist: false,
      status: 'not_found'
    };
  }
};